import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const submitContact = (data) =>
  axios.post(`${API}/contact`, data).then((r) => r.data);

export const submitApplication = (data) =>
  axios.post(`${API}/applications`, data).then((r) => r.data);

export const fetchEvents = () =>
  axios.get(`${API}/events`).then((r) => r.data);

export const fetchPopupEvent = () =>
  axios.get(`${API}/events/popup`).then((r) => r.data);

export const fetchTestimonials = () =>
  axios.get(`${API}/testimonials`).then((r) => r.data);

export const sendChatMessage = (session_id, messages) =>
  axios.post(`${API}/chat`, { session_id, messages }).then((r) => r.data);

export const uploadFile = async (file, category = "other", onProgress) => {
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);
  const r = await axios.post(`${API}/uploads`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return r.data;
};

// ----- Admin -----
// Session token lives in sessionStorage only (cleared when the browser tab closes).
const ADMIN_TOKEN_KEY = "hmf_admin_session";
localStorage.removeItem("hmf_admin_token");

export const setAdminToken = (t) => sessionStorage.setItem(ADMIN_TOKEN_KEY, t || "");
export const getAdminToken = () => sessionStorage.getItem(ADMIN_TOKEN_KEY) || "";
export const clearAdminToken = () => sessionStorage.removeItem(ADMIN_TOKEN_KEY);

const adminHeaders = () => ({ Authorization: `Bearer ${getAdminToken()}` });

export const apiError = (err, fallback = "Something went wrong. Please try again.") => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

export const adminLogin = (email, password) =>
  axios.post(`${API}/admin/auth/login`, { email, password }).then((r) => r.data);
export const adminMfaSetup = (mfa_token) =>
  axios.post(`${API}/admin/auth/mfa/setup`, { mfa_token }).then((r) => r.data);
export const adminMfaVerify = async (mfa_token, code) => {
  const r = await axios.post(`${API}/admin/auth/mfa/verify`, { mfa_token, code });
  setAdminToken(r.data.token);
  return r.data;
};
export const adminMe = () =>
  axios.get(`${API}/admin/auth/me`, { headers: adminHeaders() }).then((r) => r.data);
export const adminLogout = () =>
  axios.post(`${API}/admin/auth/logout`, {}, { headers: adminHeaders() }).catch(() => null).finally(clearAdminToken);
export const adminChangePassword = (current_password, new_password) =>
  axios.post(`${API}/admin/auth/change-password`, { current_password, new_password }, { headers: adminHeaders() }).then((r) => r.data);

export const adminListApplications = () =>
  axios.get(`${API}/admin/applications`, { headers: adminHeaders() }).then((r) => r.data);

export const adminListContacts = () =>
  axios.get(`${API}/admin/contact_messages`, { headers: adminHeaders() }).then((r) => r.data);

export const adminGetApplication = (id) =>
  axios.get(`${API}/admin/applications/${id}`, { headers: adminHeaders() }).then((r) => r.data);
export const adminRevealSensitive = (id) =>
  axios.post(`${API}/admin/applications/${id}/reveal`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminDeleteApplication = (id, confirm) =>
  axios.delete(`${API}/admin/applications/${id}`, { headers: adminHeaders(), data: { confirm } }).then((r) => r.data);

// Team & audit (owner only)
export const adminListUsers = () =>
  axios.get(`${API}/admin/users`, { headers: adminHeaders() }).then((r) => r.data);
export const adminCreateUser = (data) =>
  axios.post(`${API}/admin/users`, data, { headers: adminHeaders() }).then((r) => r.data);
export const adminSetUserActive = (id, is_active) =>
  axios.patch(`${API}/admin/users/${id}`, { is_active }, { headers: adminHeaders() }).then((r) => r.data);
export const adminResetUserMfa = (id) =>
  axios.post(`${API}/admin/users/${id}/reset-mfa`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminResetUserPassword = (id, temp_password) =>
  axios.post(`${API}/admin/users/${id}/reset-password`, { temp_password }, { headers: adminHeaders() }).then((r) => r.data);
export const adminMaintenanceStatus = () =>
  axios.get(`${API}/admin/maintenance/status`, { headers: adminHeaders() }).then((r) => r.data);
export const adminEraseLegacyCopies = (confirm) =>
  axios.post(`${API}/admin/maintenance/erase-legacy-copies`, { confirm }, { headers: adminHeaders() }).then((r) => r.data);
export const adminEraseAbandonedUploads = (confirm) =>
  axios.post(`${API}/admin/maintenance/erase-abandoned-uploads`, { confirm }, { headers: adminHeaders() }).then((r) => r.data);
export const adminListAudit = () =>
  axios.get(`${API}/admin/audit`, { headers: adminHeaders() }).then((r) => r.data);

export const adminUploadImage = async (file, category) => {
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);
  const r = await axios.post(`${API}/admin/uploads`, form, { headers: adminHeaders() });
  return r.data;
};

// Events
export const adminListEvents = () =>
  axios.get(`${API}/admin/events`, { headers: adminHeaders() }).then((r) => r.data);
export const adminCreateEvent = (data) =>
  axios.post(`${API}/admin/events`, data, { headers: adminHeaders() }).then((r) => r.data);
export const adminUpdateEvent = (id, data) =>
  axios.put(`${API}/admin/events/${id}`, data, { headers: adminHeaders() }).then((r) => r.data);
export const adminDeleteEvent = (id) =>
  axios.delete(`${API}/admin/events/${id}`, { headers: adminHeaders() }).then((r) => r.data);

// Testimonials
export const adminListTestimonials = () =>
  axios.get(`${API}/admin/testimonials`, { headers: adminHeaders() }).then((r) => r.data);
export const adminCreateTestimonial = (data) =>
  axios.post(`${API}/admin/testimonials`, data, { headers: adminHeaders() }).then((r) => r.data);
export const adminUpdateTestimonial = (id, data) =>
  axios.put(`${API}/admin/testimonials/${id}`, data, { headers: adminHeaders() }).then((r) => r.data);
export const adminDeleteTestimonial = (id) =>
  axios.delete(`${API}/admin/testimonials/${id}`, { headers: adminHeaders() }).then((r) => r.data);

export const adminDownloadFile = async (path, filename) => {
  const r = await axios.get(`${API}/admin/files/${path}`, {
    headers: adminHeaders(),
    responseType: "blob",
  });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
